import {
  type ChangeEventHandler,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react"

import { useSelector } from "@ggs/store"
import { SvgIcon } from ".."
import { type FORM_VALUES, DURATION_OPTIONS } from "./_data"
import {
  AutocompletePopover,
  Input,
  InputClearBtn,
  InputContainer,
  InputControl,
  InputIcon,
  InputLabel,
  ListItem,
  ListTitle,
} from "./_styles"

import { offset, shift, size } from "@floating-ui/react"
import { Unstable_Popup as BasePopup } from "@mui/base"
import { useMediaQuery } from "@mui/material"
import { useTheme } from "@mui/material/styles"
import { get } from "lodash"
import { useFocusWithin } from "react-aria"
import { useController, useFormContext } from "react-hook-form"

type DurationInputProps = {
  variant?: string
}
const DurationInput = forwardRef<HTMLDivElement, DurationInputProps>(({ variant }, ref) => {
  const tripSearch = useSelector((state) => state.views.index.tripSearch)
  const durationMin = get(tripSearch, "options.durationsMinMax.min", 1) // Minimum duration is 1
  const durationMax = get(tripSearch, "options.durationsMinMax.max", 999) // Maximum duration is 999 (arbitrarily large value)

  // Form context hooks
  const { resetField } = useFormContext<FORM_VALUES>()
  const {
    field: { name, value, onChange, onBlur, ref: inputRef },
    fieldState: { isDirty },
  } = useController({
    name: "duration",
  })
  const [inputValue, setInputValue] = useState(value)

  // Disable duration options that are outside the duration range
  const renderedOptions = useMemo(() => {
    return DURATION_OPTIONS.map((option) => {
      const [optionMin, optionMax] = option.value.split("-").map(Number)
      return {
        ...option,
        disabled: durationMin > optionMax || durationMax < optionMin,
      }
    })
  }, [durationMin, durationMax])

  // Popper hooks
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [budgetPopupAnchor, setBudgetPopupAnchor] = useState<HTMLElement | null>(null)
  const [isFocusWithin, setFocusWithin] = useState(false)
  const [hasBlurred, setHasBlurred] = useState(false)
  const { focusWithinProps } = useFocusWithin({
    onFocusWithin: () => setBudgetPopupAnchor(containerRef.current),
    onBlurWithin: () => {
      setBudgetPopupAnchor(null)
      if (value !== "") setHasBlurred(true)
    },
    onFocusWithinChange: (isFocusWithin) => setFocusWithin(isFocusWithin),
  })
  const budgetPopupOpen = Boolean(budgetPopupAnchor)
  const budgetPopupId = budgetPopupOpen ? "budget-popover" : undefined
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"))

  // Handle input change
  const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    onChange(event)
    setBudgetPopupAnchor(event.target.value.length > 0 ? null : containerRef.current)
  }

  // Handle clear button click
  const handleClear = () => {
    resetField(name)
    setInputValue("")
    setBudgetPopupAnchor(null)
    setFocusWithin(false)
  }

  // Handle list item click in the dropdown popover
  const handleItemClick = (option: (typeof renderedOptions)[number]) => {
    if (option.disabled) return
    if (option.label === inputValue) {
      resetField(name)
      setInputValue("")
    } else {
      onChange(option.value)
      setInputValue(option.label)
      setBudgetPopupAnchor(null)
      containerRef.current?.focus()
    }
  }

  useImperativeHandle(
    ref,
    // @ts-ignore
    () => {
      return {
        focus() {
          containerRef.current?.focus()
        },
      }
    },
    []
  )

  return (
    <>
      <InputContainer
        ref={containerRef}
        focused={isFocusWithin}
        variant={variant}
        {...focusWithinProps}
        onClick={() => {
          setFocusWithin(true)
          if (budgetPopupAnchor) return
          setBudgetPopupAnchor(containerRef.current)
        }}
        tabIndex={0}
      >
        <InputIcon>
          <SvgIcon variant="calendar" size={24} />
        </InputIcon>
        <InputControl variant={variant}>
          <InputLabel htmlFor="input-duration">Duration</InputLabel>
          <Input
            ref={inputRef}
            id="input-duration"
            name={name}
            placeholder="Select how many days"
            autoComplete="off"
            spellCheck={false}
            value={inputValue || ""}
            onChange={handleChange}
            onBlur={onBlur}
            disabled
          />
        </InputControl>
        {value !== "" && isDirty && hasBlurred && (
          <InputClearBtn
            type="reset"
            focused={isFocusWithin}
            onClick={(e) => {
              e.stopPropagation()
              handleClear()
            }}
            onMouseDown={(e) => {
              e.preventDefault()
              e.currentTarget.blur()
            }}
          >
            <span>Clear</span>
            <SvgIcon variant="close" size={8} strokeMode />
          </InputClearBtn>
        )}
        <BasePopup
          id={budgetPopupId}
          open={budgetPopupOpen}
          anchor={budgetPopupAnchor}
          placement={isDesktop && variant === "default" ? "top" : "bottom"}
          strategy="fixed"
          disablePortal
          middleware={[
            shift(),
            offset(isDesktop ? 10 : 4),
            size({
              apply({ rects, availableHeight, elements }) {
                Object.assign(elements.floating.style, {
                  minWidth: `${rects.reference.width}px`,
                  height: isDesktop ? "" : `${availableHeight - 98}px`,
                  overflow: availableHeight >= elements.floating.clientHeight ? "" : "auto",
                })
              },
            }),
          ]}
          slotProps={{
            root: {
              role: "listbox",
              style: {
                zIndex: 1,
              },
            },
          }}
        >
          <AutocompletePopover
            sx={(theme) => ({
              display: "flex",
              flexFlow: "column",
              gridGap: theme.spacing(4),
              pt: theme.spacing(6),
            })}
          >
            <ListTitle>Length of trip</ListTitle>
            {renderedOptions.map((option) => (
              <ListItem
                key={option.value}
                tabIndex={option.disabled ? -1 : 0}
                disabled={option.disabled}
                role="option"
                aria-selected={option.label === inputValue}
                selected={option.label === inputValue}
                onClick={() => handleItemClick(option)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    handleItemClick(option)
                  }
                }}
              >
                <div className="list-item__label">{option.label}</div>
              </ListItem>
            ))}
          </AutocompletePopover>
        </BasePopup>
      </InputContainer>
    </>
  )
})
DurationInput.displayName = "DurationInput"

export default DurationInput
